import React from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Divider,
  Circle,
  useColorModeValue,
  Button,
  Icon,
} from '@chakra-ui/react';
import { FaDollarSign } from 'react-icons/fa';

const VehiclePricingCard = ({ vehicleData, onRecalculate, isRecalculating }) => {
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Extract data from API response
  const valuation = vehicleData?.valuation || 0;
  const priceHistory = vehicleData?.price_history || [];
  const fetchedAt = vehicleData?.fetched_at || '';

  // Format price history data and reverse to show latest at top
  const formattedPriceHistory = priceHistory
    .map((item, index) => ({
      date: new Date(item.date).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      }),
      price: `$${item.price.toLocaleString()}`,
      highlight: index === priceHistory.length - 1, // Highlight the latest price
    }))
    .reverse(); // Reverse to show latest price at top

  // Calculate price range (min and max from history)
  const prices = priceHistory.map(item => item.price);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);

  return (
    <Box
    borderWidth="1px"
      borderColor="gray.300"
      borderRadius="md"
      overflow="hidden"
      bg={bg}
      boxShadow="sm"
      textAlign="center"
    >
      {/* Header */}
      <Flex
        justify={{ base: 'flex-start', md: 'space-between' }}
        align={{ base: 'stretch', md: 'center' }}
        direction={{ base: 'column', md: 'row' }}
        gap={{ base: 2, md: 0 }}
        bg="#212e89"
        color="white"
        px={4}
        py={2}
      >
        <Flex align="center" gap={4}>
          <Heading size={{ base: "md", md: "lg" }} fontWeight="semibold">
            Vehicle Valuation & Pricing
          </Heading>
          <Icon as={FaDollarSign} boxSize={8} />
        </Flex>
        {onRecalculate && (
          <Button
            colorScheme="whiteAlpha"
            size="sm"
            onClick={onRecalculate}
            isLoading={isRecalculating}
            loadingText="Recalculating..."
            variant="outline"
            borderColor="white"
            color="white"
            width={{ base: '100%', md: 'auto' }}
            mt={{ base: 1, md: 0 }}
            _hover={{
              bg: 'whiteAlpha.200',
            }}
          >
            Recalculate Valuation
          </Button>
        )}
      </Flex>

      <Box
        p={4}
        flex="1"
        overflow="hidden"
        display="flex"
        flexDirection="column"
      >
        <Text fontSize="sm" color="gray.600" mb={4} flexShrink={0}>
          Current market valuation and price history based on AI analysis.
          {fetchedAt &&
            ` Last updated: ${new Date(fetchedAt).toLocaleDateString()}`}
        </Text>

        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={4}
          flex={{ base: 'none', md: 1 }}
          minHeight={0}
        >
          {/* Left Side - Current Valuation */}
          <Box
            flex={{ base: 'none', md: '1' }}
            textAlign="center"
            borderRight={{ md: '1px solid' }}
            borderBottom={{ base: '1px solid', md: 'none' }}
            borderColor={borderColor}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            pb={{ base: 4, md: 0 }}
          >
            <Box
              bg="blue.500"
              color="white"
              p={3}
              borderRadius="md"
              display="inline-block"
              fontWeight="bold"
              mb={2}
              fontSize="md"
              mx="auto"
            >
              ${valuation.toLocaleString()}
            </Box>
            <Text fontSize="md" fontWeight="bold" mb={3}>
              Current Valuation
            </Text>

            {priceHistory.length > 0 && (
              <Box>
                <Text fontSize="sm" color="gray.600" mb={1}>
                  Price Range (Last 7 Days)
                </Text>
                <Text fontSize="sm" fontWeight="bold" color="blue.600">
                  ${minPrice.toLocaleString()} – ${maxPrice.toLocaleString()}
                </Text>
              </Box>
            )}
          </Box>

          {/* Right Side */}
          <Box
            flex={{ base: 'none', md: '1' }}
            display="flex"
            flexDirection="column"
            minHeight={0}
          >
            <Heading
              size="md"
              color="gray.700"
              mb={2}
              flexShrink={0}
              fontWeight="bold"
              textDecoration="underline"
            >
              Vehicle Pricing History
            </Heading>

            <Box
              flex={{ base: 'none', md: '1' }}
              overflowY={{ base: 'visible', md: 'auto' }}
              overflowX="hidden"
              pr={2}
              minHeight={0}
              maxHeight={{ base: 'none', md: '200px' }}
              css={{
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#c1c1c1',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#a8a8a8',
                },
              }}
            >
              <VStack align="stretch" spacing={1} py={1}>
                {formattedPriceHistory.length > 0 ? (
                  formattedPriceHistory.map((item, index) => (
                    <Box
                      key={index}
                      role="group"
                      px={2}
                      py={1}
                      borderRadius="md"
                      cursor="pointer"
                      transition="all 0.2s"
                      bg={item.highlight ? 'blue.50' : 'transparent'}
                      _hover={{
                        bg: 'blue.50',
                      }}
                    >
                      <HStack
                        justify="space-between"
                        align="center"
                        position="relative"
                      >
                        {/* Date */}
                        <Text
                          fontWeight={item.highlight ? 'bold' : 'normal'}
                          color={item.highlight ? 'blue.700' : 'gray.600'}
                          fontSize="sm"
                          transition="all 0.2s"
                          _groupHover={{
                            color: 'blue.700',
                            fontWeight: 'bold',
                          }}
                        >
                          {item.date}
                        </Text>

                        {/* Timeline Dot */}
                        <Box
                          position="relative"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          flex="0 0 auto"
                          px={2}
                        >
                          <Circle
                            size={item.highlight ? '12px' : '10px'}
                            bg={item.highlight ? 'blue.600' : 'gray.400'}
                            zIndex="2"
                            transition="all 0.2s"
                            _groupHover={{
                              bg: 'blue.600',
                              size: '12px',
                            }}
                          />
                          {index < formattedPriceHistory.length - 1 && (
                            <Box
                              position="absolute"
                              top="calc(50% + 5px)"
                              left="50%"
                              transform="translateX(-50%)"
                              width="1px"
                              height="20px"
                              bg="gray.300"
                              zIndex="1"
                            />
                          )}
                        </Box>

                        {/* Price */}
                        <Text
                          fontWeight={item.highlight ? 'bold' : 'normal'}
                          color={item.highlight ? 'blue.700' : 'gray.700'}
                          fontSize="sm"
                          transition="all 0.2s"
                          _groupHover={{
                            color: 'blue.700',
                            fontWeight: 'bold',
                          }}
                        >
                          {item.price}
                        </Text>
                      </HStack>
                    </Box>
                  ))
                ) : (
                  <Text color="gray.500" textAlign="center" py={4}>
                    No price history available
                  </Text>
                )}
              </VStack>
            </Box>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

export default VehiclePricingCard;
